import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

export interface LearnedRecipeSummary {
  learnedId: string;
  userId: string;
  recipeId: string;
  learnedAt: string;
}

interface UseLearnedRecipesOptions {
  userId?: string;
  admin?: boolean;
}

interface UseLearnedRecipesReturn {
  learnedRecipes: LearnedRecipeSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useLearnedRecipes(options: UseLearnedRecipesOptions = {}): UseLearnedRecipesReturn {
  const [learnedRecipes, setLearnedRecipes] = useState<LearnedRecipeSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchLearned = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setError(null);
      setLoading(true);
      const path =
        options.admin && options.userId
          ? `/arches/crafting/admin/learned/${options.userId}`
          : '/arches/crafting/learned';
      const { data } = await api.get<{
        learnedRecipes: LearnedRecipeSummary[];
        total: number;
      }>(path);
      if (fetchIdRef.current !== id) return;
      setLearnedRecipes(data.learnedRecipes);
      setTotal(data.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load learned recipes');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [options.admin, options.userId]);

  useEffect(() => {
    fetchLearned();
  }, [fetchLearned]);

  return {
    learnedRecipes,
    total,
    loading,
    error,
    refresh: fetchLearned,
  };
}
